import { type ApiClient, revisions } from '../store/backend';
import type { Revision } from '../content/revision';
import { networkState } from '../utilities/network-error-handler';

// Cache for revisions to prevent repeated fetches
interface RevisionsCacheEntry {
  revisions: Revision[];
  fetchedAt: number;
}

const revisionsCache = new Map<string, RevisionsCacheEntry>();

// Maximum age for cached revisions (5 minutes)
const CACHE_MAX_AGE = 5 * 60 * 1000;

function getCacheKey(
  entityType: string,
  entityId: string,
  organizationId: number,
  contentLastModified?: string,
): string {
  // Include contentLastModified in cache key so we re-fetch when entity changes
  return `${organizationId}:${entityType}:${entityId}:${contentLastModified || 'none'}`;
}

function getCachedRevisions(cacheKey: string): Revision[] | null {
  const cached = revisionsCache.get(cacheKey);
  if (!cached) {
    return null;
  }

  const age = Date.now() - cached.fetchedAt;
  if (age > CACHE_MAX_AGE) {
    // Cache expired
    revisionsCache.delete(cacheKey);
    return null;
  }

  return cached.revisions;
}

function setCachedRevisions(cacheKey: string, revisions: Revision[]): void {
  revisionsCache.set(cacheKey, {
    revisions,
    fetchedAt: Date.now(),
  });
}

export async function fetchRevisions(
  api: ApiClient,
  entityType: string,
  entityId: string,
  organizationId: number,
  templateDeleted: boolean,
  parentEntityId?: string,
  contentLastModified?: string,
): Promise<Revision[]> {
  // Check cache first
  const cacheKey = getCacheKey(entityType, entityId, organizationId, contentLastModified);
  const cached = getCachedRevisions(cacheKey);
  if (cached) {
    console.log(`[fetchRevisions] Using cached revisions for ${entityType} ${entityId}`);
    return cached;
  }

  // Skip API call if offline - return empty revisions
  if (!networkState.isOnline) {
    console.info(`Skipping revisions fetch for ${entityType}/${entityId} (offline mode)`);
    return [];
  }

  try {
    console.log(`[fetchRevisions] Fetching revisions from API for ${entityType} ${entityId}`);
    const r = await revisions(api, entityType, entityId, organizationId, parentEntityId);

    const suggested = r.find((i) => i.status === 'SUGGESTED');
    const revisionsNotSuggested = r.filter(function (e) {
      return e.status !== 'SUGGESTED';
    });
    const isSuggestedInUse =
      suggested !== undefined &&
      revisionsNotSuggested.filter(function (e) {
        return e.name === suggested.name && e.content === suggested.content + 'W';
      }).length > 0;
    const excludeSuggested = !templateDeleted && isSuggestedInUse;

    const filtered = excludeSuggested ? revisionsNotSuggested : r;

    const result = filtered.map((v) => ({
      changedBy: v.changedBy ?? '',
      dateTime: v.dateTime ?? '',
      status: v.status ?? '',
      name: v.name ?? '',
      content: v.content ?? '',
      differences: {
        name: v.nameDifference ?? '',
        content: v.contentDifference ?? '',
      },
    }));

    // Cache the result
    setCachedRevisions(cacheKey, result);

    return result;
  } catch (error) {
    // Return empty revisions when network error occurs
    // This allows documents to load without revision history
    console.info(`Could not fetch revisions for ${entityType}/${entityId} (network error):`, error);
    return [];
  }
}
